import { createCloudAdminRuntime } from './cloudAdminRuntime';
import { createPluginAdminRuntime } from './pluginAdminRuntime';
import { createRuntimeSearchSettingsClient } from './runtimeSearchSettingsClient';
import { hasMarketplacePluginContract } from './pluginCompatibility';
import { createAdminCompatibilityModel, supportsDescriptionCatalogWorkflow } from './adminCompatibilityModel';

const PRODUCT_PROFILES = Object.freeze({
  plugin: Object.freeze({ id: 'plugin' }),
  cloud: Object.freeze({ id: 'cloud' }),
});

/** Composition-root helper. Product selection happens once per shell. */
export function createProductRuntime({
  productId,
  gatewayClient,
  bridgeClient,
  webserviceClient,
  capabilities = {},
  platform = null,
  moduleVersion = null,
}) {
  if (!PRODUCT_PROFILES[productId]) {
    throw new Error(`Unknown admin product: "${productId}"`);
  }

  let runtime = null;
  let effectiveCapabilities = { ...capabilities };
  const descriptionPluginLane = productId === 'plugin' && platform === 'prestashop';
  [
    'descriptionParametersV1',
    'descriptionParametersCatalogPreparationV1',
    'descriptionParametersLifecycleV1',
    'descriptionParametersNormalizationV1',
    'descriptionParametersNormalizationV2',
  ].forEach((capability) => {
    if (Object.prototype.hasOwnProperty.call(capabilities, capability)) {
      effectiveCapabilities[capability] = descriptionPluginLane && capabilities[capability] === true;
    }
  });

  if (Object.prototype.hasOwnProperty.call(capabilities, 'descriptionParametersV1')) {
    effectiveCapabilities.descriptionParametersV1 = descriptionPluginLane
      && supportsDescriptionCatalogWorkflow(capabilities);
  }

  if (productId === 'plugin') {
    // Plugin pages are exposed only through the versioned bridge handshake.
    // Do not infer broad product support from unrelated legacy flags: an older
    // bridge may implement operator state without implementing V2 settings or
    // the gateway-backed editor/report contracts.
    // Marketplace PrestaShop 1.0.4+ already implements the bridge, gateway,
    // preview and production publication contracts. The explicit per-page and
    // shared-runtime capability names were added later, so project the proven
    // marketplace contract without blocking released customer installations.
    const marketplacePluginContract = hasMarketplacePluginContract({
      platform,
      moduleVersion,
      capabilities,
    });
    const sharedAdminRuntimeV1 = capabilities.sharedAdminRuntimeV1 === true;

    effectiveCapabilities.sharedAdminRuntimeV1 = sharedAdminRuntimeV1;
    if (sharedAdminRuntimeV1 || marketplacePluginContract) {
      effectiveCapabilities.artifactMutationV1 = true;
    }
    effectiveCapabilities.settingsV2 = capabilities.settingsV2 === true
      || marketplacePluginContract;
    effectiveCapabilities.searchCustomizationV1 = capabilities.searchCustomizationV1 === true
      || marketplacePluginContract;
    effectiveCapabilities.analyticsV1 = capabilities.analyticsV1 === true
      || marketplacePluginContract;
    effectiveCapabilities.analyticsPopularProductsV1 = capabilities.analyticsPopularProductsV1 === true
      || marketplacePluginContract;
    effectiveCapabilities.analyticsQueryDetailV1 = capabilities.analyticsQueryDetailV1 === true;
    effectiveCapabilities.analyticsReportExportV1 = capabilities.analyticsReportExportV1 === true;
    effectiveCapabilities.deferredReindexV1 = capabilities.deferredReindexV1 === true;
    effectiveCapabilities.facetPolicyEditorV1 = capabilities.facetPolicyEditorV1 === true;
    effectiveCapabilities.indexingV1 = Boolean(
      (sharedAdminRuntimeV1 || marketplacePluginContract) && capabilities.indexProgressV1,
    );
    effectiveCapabilities.emptyQueryV1 = capabilities.emptyQueryV1 === true
      || marketplacePluginContract;
    effectiveCapabilities.facetValueMappingsV1 = capabilities.facetValueMappingsV1 === true;
    effectiveCapabilities.merchandising_search_terms_v1 = capabilities.merchandising_search_terms_v1 === true;
    if (Object.prototype.hasOwnProperty.call(capabilities, 'merchandising_advanced_conditions_v1')) {
      effectiveCapabilities.merchandising_advanced_conditions_v1 = capabilities.merchandising_advanced_conditions_v1 === true;
    }
    if (Object.prototype.hasOwnProperty.call(capabilities, 'cardTemplateEditor')) {
      effectiveCapabilities.cardTemplateEditor = capabilities.cardTemplateEditor === true
        || marketplacePluginContract;
    } else if (marketplacePluginContract) {
      effectiveCapabilities.cardTemplateEditor = true;
    }
  } else {
    effectiveCapabilities.settingsV2 = true;
    effectiveCapabilities.searchCustomizationV1 = true;
    effectiveCapabilities.analyticsV1 = true;
    effectiveCapabilities.analyticsPopularProductsV1 = false;
    effectiveCapabilities.analyticsQueryDetailV1 = true;
    effectiveCapabilities.analyticsReportExportV1 = false;
    effectiveCapabilities.deferredReindexV1 = false;
    effectiveCapabilities.facetPolicyEditorV1 = true;
    effectiveCapabilities.cloudDataSettingsV1 = true;
    effectiveCapabilities.indexingV1 = true;
    effectiveCapabilities.emptyQueryV1 = true;
    effectiveCapabilities.emptyQueryVisualV1 = true;
    effectiveCapabilities.facetValueMappingsV1 = true;
    effectiveCapabilities.facet_policy_v1 = true;
    effectiveCapabilities.facet_numeric_v1 = true;
    effectiveCapabilities.facet_order_v1 = true;
    effectiveCapabilities.facet_policy_preview_v1 = true;
    effectiveCapabilities.cardTemplateEditor = true;
    effectiveCapabilities.merchandising_bridge_v1 = true;
    effectiveCapabilities.merchandising_rules_v1 = true;
    effectiveCapabilities.category_priority_v2 = true;
    effectiveCapabilities.merchandising_preview_v1 = true;
    effectiveCapabilities.merchandising_explain_v1 = true;
    effectiveCapabilities.merchandising_query_groups_v1 = true;
    effectiveCapabilities.merchandising_csv_v1 = true;
    effectiveCapabilities.merchandising_search_terms_v1 = true;
    effectiveCapabilities.merchandising_advanced_conditions_v1 = true;
    effectiveCapabilities.synonymSuggestionsV1 = true;
    effectiveCapabilities.suggestionOverridesV1 = true;
    effectiveCapabilities.catalogSelectorsV1 = true;
    effectiveCapabilities.availabilityRulesV1 = true;
    effectiveCapabilities.payloadProvidersV1 = true;
    effectiveCapabilities.variantPresentationSettingsV1 = true;
    effectiveCapabilities.variantGroupingV1 = true;
  }

  if (productId === 'plugin' && gatewayClient) {
    runtime = createPluginAdminRuntime({
      gatewayClient,
      bridgeClient,
      artifactMutationEnabled: effectiveCapabilities.artifactMutationV1 === true,
    });
  } else if (productId === 'cloud' && webserviceClient) {
    runtime = createCloudAdminRuntime({ webserviceClient });
  }

  if (!runtime) {
    return {
      productProfile: PRODUCT_PROFILES[productId],
      runtime: null,
      searchSettingsClient: null,
      capabilities: effectiveCapabilities,
      compatibility: createAdminCompatibilityModel({
        productId,
        platform,
        capabilities: effectiveCapabilities,
      }),
    };
  }

  return {
    productProfile: PRODUCT_PROFILES[runtime.productId],
    runtime,
    searchSettingsClient: createRuntimeSearchSettingsClient(runtime),
    capabilities: effectiveCapabilities,
    compatibility: createAdminCompatibilityModel({
      productId,
      platform,
      capabilities: effectiveCapabilities,
    }),
  };
}
